import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { request, requestBlob } from "utils/request";
import { toSetupSendBody } from "app/containers/Jobs/utils/setupSend";
import { TABS, isPaginatedTab } from "./utils/tabs";
import { adaptJobList, adaptJobDetail } from "./utils/adaptJobs";

export const JOBS_PER_PAGE = 10;

const base = process.env.REACT_APP_API_URL;

export const fetchJobs = createAsyncThunk(
  "jobs/fetchJobs",
  async ({ tab = TABS.UPCOMING, page = 1 } = {}, { rejectWithValue }) => {
    try {
      // `page`/`page_size` are only meaningful on the `all` tab — the others
      // are unpaginated server-side and ignore them. Ordering is server-side
      // too, so there is no `ordering` param.
      const params = new URLSearchParams({ tab });
      if (isPaginatedTab(tab)) {
        params.set("page", page);
        params.set("page_size", JOBS_PER_PAGE);
      }
      const payload = await request(`${base}/jobs/?${params}`, {
        method: "GET",
      });
      return { ...adaptJobList(payload, tab), tab, page };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load jobs");
    }
  }
);

const lookupAppointmentId = async (detail) => {
  const params = new URLSearchParams({ start: detail.date, end: detail.date });
  const data = await request(
    `${base}/calendar/appointments/by_date_range/?${params}`,
    { method: "GET" }
  );
  const matches = (data.appointments || []).filter(
    (appointment) => appointment.job?.id === detail.id
  );

  return matches.length === 1 ? matches[0].id : null;
};

export const fetchJobDetail = createAsyncThunk(
  "jobs/fetchJobDetail",
  async (id, { rejectWithValue }) => {
    try {
      const payload = await request(`${base}/jobs/${id}/`, { method: "GET" });
      const detail = adaptJobDetail(payload, id);
      if (detail.appointment_id || !detail.date) return detail;
      // A failed lookup must not fail the page: every card but Edit Booking
      // renders fine without an appointment id.
      const appointmentId = await lookupAppointmentId(detail).catch(() => null);
      return { ...detail, appointment_id: appointmentId };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load job");
    }
  }
);

export const deleteJob = createAsyncThunk(
  "jobs/deleteJob",
  async (id, { rejectWithValue }) => {
    try {
      await request(`${base}/jobs/${id}/`, { method: "DELETE" });
      return id;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete job");
    }
  }
);

// Composite contract + payment-request send. Each requested track reports its
// own {ok, ...} and runs independently — one failing never blocks the other.
export const setupSendJob = createAsyncThunk(
  "jobs/setupSend",
  async (
    {
      id,
      sendContract = false,
      sendPaymentRequest = false,
      contractTemplateId,
    },
    { rejectWithValue }
  ) => {
    try {
      return await request(`${base}/jobs/${id}/setup-send/`, {
        method: "POST",
        body: JSON.stringify(
          toSetupSendBody({
            sendContract,
            sendPaymentRequest,
            contractTemplateId,
          })
        ),
      });
    } catch (error) {
      return rejectWithValue(error.message || "Failed to send");
    }
  }
);

// --- Job-scoped payment actions (PaymentCollectAPI) -------------------------
// All three reject with the backend's own `user_error_msg` where there is one,
// because its 409s are the informative case: "already has an active payment",
// "not cancellable", "not undoable". A generic fallback would hide why.
const paymentError = (error, fallback) =>
  error?.user_error_msg || error?.message || fallback;

// Record an off-Stripe payment. `amountCents` is optional — the backend falls
// back to the job's quote when it is omitted.
export const markJobPaid = createAsyncThunk(
  "jobs/markPaid",
  async ({ id, amountCents } = {}, { rejectWithValue }) => {
    try {
      return await request(`${base}/payments/collect/payments/`, {
        method: "POST",
        body: JSON.stringify({
          job_id: id,
          ...(amountCents != null ? { amount_cents: amountCents } : {}),
        }),
      });
    } catch (error) {
      return rejectWithValue(
        paymentError(error, "Couldn't mark this job as paid.")
      );
    }
  }
);

// Undo a manual payment — hard-deletes the row, back to "No Payment".
export const undoManualPayment = createAsyncThunk(
  "jobs/undoManualPayment",
  async (paymentId, { rejectWithValue }) => {
    try {
      await request(`${base}/payments/collect/payments/${paymentId}/`, {
        method: "DELETE",
      });
      return paymentId;
    } catch (error) {
      return rejectWithValue(paymentError(error, "Couldn't undo the payment."));
    }
  }
);

// Cancel Request — deactivates the client's Stripe link and detaches the row.
// Not gated on tier: a downgraded user must still be able to stop a live link.
export const cancelPaymentRequest = createAsyncThunk(
  "jobs/cancelPaymentRequest",
  async (paymentId, { rejectWithValue }) => {
    try {
      await request(`${base}/payments/collect/payment-requests/${paymentId}/`, {
        method: "DELETE",
      });
      return paymentId;
    } catch (error) {
      return rejectWithValue(
        paymentError(error, "Couldn't cancel the payment request.")
      );
    }
  }
);

// --- Contract actions (ContractAPI) -----------------------------------------
// Revoke and Reset take the same body: a structured reason_code plus optional
// free-text note (which the backend stores as void_reason and the Revoked card
// renders as its headline).
const contractError = (error, fallback) =>
  error?.user_error_msg || error?.message || fallback;

export const revokeContract = createAsyncThunk(
  "jobs/revokeContract",
  async ({ contractId, reasonCode, note } = {}, { rejectWithValue }) => {
    try {
      return await request(`${base}/contracts/${contractId}/revoke/`, {
        method: "POST",
        body: JSON.stringify({
          reason_code: reasonCode,
          ...(note ? { note } : {}),
        }),
      });
    } catch (error) {
      return rejectWithValue(
        contractError(error, "Couldn't revoke the contract.")
      );
    }
  }
);

export const resetContractStatus = createAsyncThunk(
  "jobs/resetContractStatus",
  async ({ contractId, reasonCode, note } = {}, { rejectWithValue }) => {
    try {
      return await request(`${base}/contracts/${contractId}/reset-status/`, {
        method: "POST",
        body: JSON.stringify({
          reason_code: reasonCode,
          ...(note ? { note } : {}),
        }),
      });
    } catch (error) {
      return rejectWithValue(
        contractError(error, "Couldn't reset the contract status.")
      );
    }
  }
);

// Streams the signed PDF and hands the browser a download. Uses requestBlob —
// the standard wrapper would run .text() over the bytes and corrupt them.
export const downloadContract = createAsyncThunk(
  "jobs/downloadContract",
  async (contractId, { rejectWithValue }) => {
    try {
      const blob = await requestBlob(
        `${base}/contracts/${contractId}/download/`,
        { method: "GET" }
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `contract-${contractId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return contractId;
    } catch (error) {
      return rejectWithValue(
        contractError(error, "Couldn't download the contract.")
      );
    }
  }
);

// Re-renders the contract the client received, from the send-time HTML
// snapshot, and hands back an object URL the caller opens in a new tab.
//
// This is NOT download/: that streams BoldSign's signed PDF and 404s
// contract.no_signed_pdf for the whole time a contract is out for signature,
// which is exactly when the inspector wants to look at it. preview/ makes no
// BoldSign call and answers at every status.
export const previewContractPdf = createAsyncThunk(
  "jobs/previewContractPdf",
  async (contractId, { rejectWithValue }) => {
    try {
      // requestBlob, not request: the response is PDF bytes, and the standard
      // wrapper would run .text() over them.
      const blob = await requestBlob(
        `${base}/contracts/${contractId}/preview/`,
        { method: "GET" }
      );
      return window.URL.createObjectURL(blob);
    } catch (error) {
      return rejectWithValue(
        contractError(error, "Couldn't preview this contract.")
      );
    }
  }
);

const initialState = {
  activeTab: TABS.UPCOMING,
  list: [],
  jobsCount: 0,
  currentPage: 1,
  counts: { [TABS.UPCOMING]: 0, [TABS.NEEDS_ATTENTION]: 0, [TABS.ALL]: 0 },
  getJobsStatus: "idle",
  getJobsError: null,
  // requestId of the in-flight fetch. Responses can arrive out of order — a
  // slow `needs_attention` (unpaginated, up to 500 rows) can land after a fast
  // `all` — so anything that is not the newest request is dropped rather than
  // allowed to overwrite the list and snap activeTab back.
  currentRequestId: null,

  detail: null,
  getJobDetailStatus: "idle",
  getJobDetailError: null,
  // Same out-of-order protection as the list. refreshDetail() fires after every
  // send, payment and contract action and when either modal closes, so two
  // fetches are easily in flight at once and the older one must not win.
  currentDetailRequestId: null,
  // Which job the current detail request is for. The calendar and the Jobs
  // list load details into this slice too, so Job Detail checks it before
  // painting a detail (or an error) as its own.
  detailJobId: null,

  deleteJobStatus: "idle",
  deleteJobError: null,
};

const jobsSlice = createSlice({
  name: "jobs",
  initialState,
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
      state.currentPage = 1;
      state.list = [];
      state.jobsCount = 0;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
      state.list = [];
      state.jobsCount = 0;
    },
    clearJobDetail: (state) => {
      state.detail = null;
      state.getJobDetailStatus = "idle";
      state.getJobDetailError = null;
      // Unmounting invalidates whatever is in flight; without this a response
      // that lands after the page closes repopulates the cleared detail.
      state.currentDetailRequestId = null;
      state.detailJobId = null;
    },
    clearDeleteStatus: (state) => {
      state.deleteJobStatus = "idle";
      state.deleteJobError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobs.pending, (state, action) => {
        state.getJobsStatus = "loading";
        state.getJobsError = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.getJobsStatus = "success";
        state.list = action.payload.results || [];
        state.jobsCount = action.payload.count || 0;
        // adaptJobList always returns a counts object, so a plain truthiness
        // check would let an omitted `counts` overwrite the badges with zeroes
        // while rows are visibly present.
        const { counts } = action.payload;
        if (counts && Object.keys(counts).length) {
          state.counts = counts;
        }
        state.activeTab = action.payload.tab;
        state.currentPage = action.payload.page;
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.getJobsStatus = "failed";
        state.getJobsError = action.payload;
      })

      .addCase(fetchJobDetail.pending, (state, action) => {
        state.getJobDetailStatus = "loading";
        state.getJobDetailError = null;
        state.currentDetailRequestId = action.meta.requestId;
        state.detailJobId = action.meta.arg;
      })
      .addCase(fetchJobDetail.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentDetailRequestId) return;
        state.getJobDetailStatus = "success";
        state.detail = action.payload;
      })
      .addCase(fetchJobDetail.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentDetailRequestId) return;
        state.getJobDetailStatus = "failed";
        state.getJobDetailError = action.payload;
      })

      .addCase(deleteJob.pending, (state) => {
        state.deleteJobStatus = "loading";
        state.deleteJobError = null;
      })
      .addCase(deleteJob.fulfilled, (state, action) => {
        state.deleteJobStatus = "success";
        state.list = state.list.filter((job) => job.id !== action.payload);
        state.jobsCount = Math.max(0, state.jobsCount - 1);
      })
      .addCase(deleteJob.rejected, (state, action) => {
        state.deleteJobStatus = "failed";
        state.deleteJobError = action.payload;
      });
  },
});

export const {
  setActiveTab,
  setCurrentPage,
  clearJobDetail,
  clearDeleteStatus,
} = jobsSlice.actions;

export default jobsSlice.reducer;
